/**
 * The phone Book screen (Gabriel 2026-10-21, "yes on the phone book screen
 * mockup"; docs/mockups/telify-web/phone-book-screen.html): below 768px `/services` and
 * `/events` are the app's Book screen: its tab frame (ClientShellFrame), its header
 * (CatalogHeader) and its tab bodies (BookCatalogScreen), fed the website's reads
 * (shared-client-screens D3, rows B1 to B6). This file is the website's data part.
 *
 * The website's slots: the bar asks a visitor to sign in (B1), the back arrow opens the
 * studio's home (B2), and each tab is its own address, switching replaces it, so Back from a
 * card's page returns to the tab (B3). As the app, the screen spins until its list is in (B5),
 * and a signed-in client's booked event opens their booking (B4). Nothing here reaches Telegram
 * (scripts/check-web-no-telegram.mjs).
 */
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { ClientShellFrame } from '@/components/client/ClientShell/ClientShellFrame';
import { BookCatalogScreen } from '@/pages/BookSessionPage/BookCatalogScreen';
import { CatalogHeader, type CatalogTab } from '@/pages/BookSessionPage/CatalogHeader';

import { useSiteEvents, useSiteServices, useSiteStudio } from '../api/site';
import { toHubEvent, toHubService } from './appShapes';
import { PhoneBar } from './PhoneTabsLayout';

const statusOf = (q: { isPending: boolean; isError: boolean }) => (q.isPending ? 'loading' : q.isError ? 'error' : 'ready') as 'loading' | 'error' | 'ready';

export function PhoneBookScreen({ tab }: { tab: CatalogTab }) {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLElement>(null);
  const studio = useSiteStudio().data;
  // Both lists are read at once, so switching tabs (a new address) draws the other at once.
  const servicesQuery = useSiteServices();
  const eventsQuery = useSiteEvents();
  const siteServices = servicesQuery.data;
  const siteEvents = useMemo(() => (eventsQuery.data?.pages ?? []).flatMap((p) => p.events), [eventsQuery.data]);
  const services = useMemo(() => (siteServices ?? []).map(toHubService), [siteServices]);
  const events = useMemo(() => siteEvents.map(toHubEvent), [siteEvents]);
  const ready = !(tab === 'services' ? servicesQuery : eventsQuery).isPending;

  return (
    <ClientShellFrame
      status={ready ? 'ready' : 'loading'}
      dashboard={undefined}
      isHome={false}
      headerSlot={
        ready ? (
          <CatalogHeader
            activeTab={tab}
            onTabChange={(next) => navigate(next === 'events' ? '/events' : '/services', { replace: true })}
            onBack={() => navigate('/')}
          />
        ) : null
      }
      contentRef={contentRef}
      nav={<PhoneBar />}
      scrollKey={tab}
    >
      <BookCatalogScreen
        tab={tab}
        paused={studio?.acceptingBookings === false}
        services={{
          status: statusOf(servicesQuery),
          items: services,
          onRetry: () => void servicesQuery.refetch(),
          onOpen: (s) => {
            const path = siteServices?.find((x) => x.id === s.id)?.path;
            if (path) navigate(path);
          },
        }}
        events={{
          status: statusOf(eventsQuery),
          items: events,
          onRetry: () => void eventsQuery.refetch(),
          onOpen: (e) => {
            const path = siteEvents.find((x) => x.id === e.id)?.path;
            if (path) navigate(path);
          },
          onOpenBooking: (id) => navigate(`/account/bookings/${encodeURIComponent(id)}`),
          more: { hasNext: eventsQuery.hasNextPage, loading: eventsQuery.isFetchingNextPage, load: () => void eventsQuery.fetchNextPage() },
        }}
      />
    </ClientShellFrame>
  );
}
